import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { PropertiesService } from 'src/app/services/properties.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminClientPropertyDialogComponent } from './admin-client-property-dialog/admin-client-property-dialog.component';
import { DataTableDirective } from 'angular-datatables';
import { DatatablesHelper, FormatHelper, TextHelper } from 'src/app/helpers';
import { ClientProperty, User } from 'src/app/models';
import { Clipboard } from '@angular/cdk/clipboard';
import { AdminService } from 'src/app/services/admin.service';

type UIProperty = ClientProperty & {
  user?:       User;
  duplicates?: string[] // ids
}

@Component({
  selector: 'app-admin-client-properties',
  templateUrl: './admin-client-properties.component.html',
  styleUrls: ['./admin-client-properties.component.css']
})
export class AdminClientPropertiesComponent implements OnInit, OnDestroy {
  // readonly COLS_HIDDEN_BY_DEFAULT = []; // NOTE: keep commented out, use for local testing only
  readonly COLS_HIDDEN_BY_DEFAULT = [0, 1];
  readonly COLS_ORDER_BY_DEFAULT = [[2, 'desc']]; // by entry date desc
  readonly COLS_CONTACT_INFORMATION = [8, 9, 10, 11];

  allProperties: any[] = [];
  loaded = false;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, {static: false})
  dtElement!: DataTableDirective;

  constructor(
    private propertiesService: PropertiesService,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService,
    private dialog: MatDialog,
    private clipboard: Clipboard
  ) {}

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      columnDefs: [
        {
          // Indexes of columns to be hidden by default
          "targets": this.COLS_HIDDEN_BY_DEFAULT,
          "visible": false
        }
      ],

      order: this.COLS_ORDER_BY_DEFAULT,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        // 'colvis',
        {
          extend: 'colvis',
          columns: (idx: number) => [0, 1].includes(idx) === false
        },
        'excel'
      ]
    });
  }

  private async loadTable() {
    // Get all the properties
    const query = {
      getusers:        true,
      getduplicates:   true,
      getmoveinsurvey: true,
    };

    const res: any = await this.propertiesService.getAllClientProperties(query).toPromise();

    this.allProperties = res.properties || [];

    this.propertiesService.properties = this.allProperties;
    this.propertiesService.clientProperties = true;

    this.toggleIssuesCol();

    if (this.loaded) {
      // Manually rerender table after changes
      this.rerender();
    } else {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  private toggleIssuesCol(): void {
    const showIssues = this.allProperties.some(property => this.issuesFoundBy(property).count > 0);

    const colsHidden = this.dtOptions.columnDefs![0];

    if (showIssues) {
      const colsToShow = [0, ...this.COLS_CONTACT_INFORMATION];

      colsHidden.targets = this.COLS_HIDDEN_BY_DEFAULT.filter(col => colsToShow.includes(col) === false);

      this.dtOptions.order = [...this.COLS_ORDER_BY_DEFAULT];
      this.dtOptions.order.unshift([1, 'desc']); // put duplicates on top
    }
    else {
      colsHidden.targets = this.COLS_HIDDEN_BY_DEFAULT;
      this.dtOptions.order = this.COLS_ORDER_BY_DEFAULT;
    }
  }

  private rerender(): void {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  editProperty(property: any){
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Edit Client Property',

      // Gotta send all the property data to the dialog to prefill
      date:         property.entryDate,
      isRented:     property.isRented,
      rentAmount:   property.rentAmount,
      ownerName:    property.ownerName,
      managerName:  property.managerName,
      contactPhone: property.contactPhone,
      email:        property.email,
      address:      property.address,
      city:         property.city,
      zipCode:      property.zipCode,
      unitNumber:   property.unitNumber,
      bedrooms:     property.bedrooms,
      bathrooms:    property.bathrooms,
      latitude:     property.latitude,
      longitude:    property.longitude,
    };

    // Open dialog
    const dialogRef = this.dialog.open(AdminClientPropertyDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      window.scroll(0,0);
      // Update Property
      this.propertiesService.updateClientProperty(property._id, propertyInfo).toPromise().then((data: any) => {

        // Rerender manually
        this.loadTable();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Property updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });
        }
      });
    });
  }

  deleteProperty(property: UIProperty){
    if (confirm(`Are you sure you want to permanently delete client property at address ${property.address}?`) !== true) {
      return;
    }

    // Delete Property
    this.propertiesService.deleteClientProperty(property._id).toPromise().then((data: any) => {

      // Rerender manually
      this.loadTable();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show("Property deleted.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  issuesFoundBy(property: UIProperty) {
    const duplicates = this.duplicatesFoundBy(property);
    const count = duplicates;
    return { duplicates, count };
  }

  duplicatesFoundBy(property: UIProperty) {
    return property.duplicates?.length || 0;
  }

  duplicatesTooltip(property: UIProperty): string {
    const duplicates = this.duplicatesFoundBy(property);

    let result = '';

    if (duplicates > 0) {
      result += `${FormatHelper.pluralize(duplicates, 'duplicate')} found by this address. \n`;
    }

    return result;
  }

  async copyGeoJsonLink() {
    const link = await this.adminService.getGeoJsonLink('clientProperties').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  async copyGeoRssLink() {
    const link = await this.adminService.getGeoRssLink('clientProperties').toPromise();

    await TextHelper.copy(link, this.clipboard);

    // Success
    this.flashMessage.show("Link copied to clipboard.", {
      cssClass: 'alert-success',
      timeout: 3000
    });
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
